<template>
  <div class="request-dialog-mask" :class="{ 'request-dialog-mask--sp': isSpScreen }" @click.self="onMaskClick">
    <div
      class="request-dialog"
      :class="{ 'request-dialog--work': step === 'work' }"
      role="dialog"
      aria-modal="true"
      :aria-label="headerTitle"
      @click.stop
    >
      <div class="request-dialog__header" :class="{ 'request-dialog__header--variant': step !== 'work' }">
        <button
          v-if="step !== 'work' || isSpScreen"
          type="button"
          class="request-dialog__back"
          :aria-label="$t('assistantCatHome.back')"
          @click="onHeaderBack"
        >
          <icon-arrow-left />
        </button>
        <h2 class="request-dialog__title">{{ headerTitle }}</h2>
        <button
          v-if="!isSpScreen"
          type="button"
          class="request-dialog__close"
          :aria-label="$t('common.close')"
          @click="$emit('close')"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <div v-if="step === 'work'" class="request-dialog__body">
        <assistant-cat-home-work-select-prompt :message="prompt" :writer-type="writerType" />

        <assistant-cat-home-work-toolbar
          :search-query="searchQuery"
          :search-expanded="searchExpanded"
          :has-active-filter="hasActiveFilter"
          @update:searchQuery="searchQuery = $event"
          @update:searchExpanded="searchExpanded = $event"
          @open-filter="onOpenFilter"
        />

        <div v-if="isLoadingNovels" class="request-dialog__status">
          {{ $t("assistantCatHome.workSelect.loading") }}
        </div>
        <div v-else-if="isOpeningNovel" class="request-dialog__status">
          {{ $t("assistantCatHome.workSelect.loading") }}
        </div>
        <div v-else-if="loadError" class="request-dialog__status">
          <p>{{ $t("assistantCatHome.workSelect.loadError") }}</p>
          <button type="button" class="request-dialog__retry" @click="loadNovels">
            {{ $t("assistantCatHome.connectionError.retry") }}
          </button>
        </div>
        <div v-else-if="filteredNovels.length === 0" class="request-dialog__status">
          {{ $t("assistantCatHome.workSelect.empty") }}
        </div>
        <div v-else class="request-dialog__novels">
          <assistant-cat-home-work-list-item
            v-for="(novel, index) in filteredNovels"
            :key="novel.novelId"
            class="request-dialog__novel"
            :class="{ 'request-dialog__novel--stripe': index % 2 === 1 }"
            :novel="novel"
            :disabled="isOpeningNovel"
            :is-script-mode-masked="isNovelScriptModeMasked(novel)"
            @select="onSelectNovel(novel)"
          />
        </div>
      </div>

      <div
        v-else-if="resultAssist"
        class="request-dialog__body request-dialog__body--embed"
      >
        <assistant-cat-home-result-dialog embedded :assist="resultAssist" @close="onCloseResult" />
      </div>

      <div
        v-else-if="step === 'manuscript' && selectedNovel && contentTab === 'request'"
        class="request-dialog__body request-dialog__body--embed"
      >
        <manuscript-select-content
          :novel-id="selectedNovel.novelId"
          :request-type="manuscriptRequestType"
          standalone
          @request-succeeded="onRequestSucceeded"
        />
      </div>

      <div
        v-else-if="step === 'theme' && selectedNovel && contentTab === 'request'"
        class="request-dialog__body request-dialog__body--embed"
      >
        <div v-if="isLoadingTheme" class="request-dialog__status">
          {{ $t("assistantCatHome.workSelect.loading") }}
        </div>
        <theme-review-confirm-content
          v-else
          :novel-id="selectedNovel.novelId"
          :assist-kind="themeKind"
          standalone
          @back="onBackToWork"
          @request-succeeded="onRequestSucceeded"
        />
      </div>

      <div
        v-else-if="step !== 'work' && contentTab === 'history'"
        class="request-dialog__body request-dialog__body--embed request-dialog__body--history"
      >
        <history-list-content
          :function-type="assistFunctionType"
          :navigate-on-click="false"
          :empty-text="$t('assistantCatHome.history.empty')"
          :empty-subtext="$t('assistantCatHome.history.emptyHint')"
          @select="onHistorySelect"
        />
      </div>

      <assistant-cat-home-assist-tabs
        v-if="step !== 'work' && !resultAssist"
        :active-tab="contentTab"
        @update:activeTab="contentTab = $event"
      />
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import AssistantCatHomeAssistTabs from "@/components/molecules/AssistantCatHomeAssistTabs.vue";
import AssistantCatHomeWorkListItem from "@/components/molecules/AssistantCatHomeWorkListItem.vue";
import AssistantCatHomeWorkSelectPrompt from "@/components/molecules/AssistantCatHomeWorkSelectPrompt.vue";
import AssistantCatHomeWorkToolbar from "@/components/molecules/AssistantCatHomeWorkToolbar.vue";
import HistoryListContent from "@/components/molecules/HistoryListContent.vue";
import ManuscriptSelectContent from "@/components/molecules/ManuscriptSelectContent.vue";
import ThemeReviewConfirmContent from "@/components/molecules/ThemeReviewConfirmContent.vue";
import AssistantCatHomeResultDialog from "@/components/ui/AssistantCatHomeResultDialog.vue";
import NovelFilterDialog, { NovelFilterDialogResult } from "@/components/ui/dialogs/NovelFilterDialog.vue";
import { AssistantCatHomeAction, isScriptModeMaskedAction } from "@/constants/assistantCatHome";
import {
  assistantCatHomeFunctionType,
  assistantCatHomeManuscriptRequestType,
  assistantCatHomeRequestTitleKey,
  assistantCatHomeThemeKind,
  assistantCatHomeWorkSelectPromptKey,
  AssistantCatHomeAssistTab,
  AssistantCatHomeManuscriptRequestType,
  AssistantCatHomeRequestStep,
  AssistantCatHomeThemeKind,
} from "@/constants/assistantCatHomeRequest";
import { AssistClient } from "@/lib/clients/assistClient";
import { isScriptModeNovel, Novel, NovelSetting } from "@/lib/models";
import { Assist, AssistFunctionType } from "@/lib/models/assist";
import { NovelGroupSummary } from "@/lib/models/novelGroup";
import { HistoryItem } from "@/store/modules/assistHistory";
import { showNotifyDialog } from "@/lib/dialog";
import { Dialog } from "@/lib/utils";
import { logError } from "@/lib/utils/logger";
import { filterAssistantCatHomeNovels, hasAssistantCatHomeWorkFilters } from "@/lib/utils/assistantCatHomeWorkFilter";
import { sortAndPinNovels } from "@/lib/utils/novelListSort";
import { validateThemeAssistHome } from "@/lib/utils/themeAssistHomeValidation";
import { THEME_ASSIST_MAX_TOTAL_CHARS } from "@/constants/themeReview";
import { useThemeStore } from "@/stores/theme";
import spScreenMixin from "@/mixins/spScreenMixin";
import IconArrowLeft from "~icons/figma/ArrowLeft";

export default Vue.extend({
  name: "AssistantCatHomeRequestDialog",
  mixins: [spScreenMixin],
  components: {
    AssistantCatHomeAssistTabs,
    AssistantCatHomeWorkListItem,
    AssistantCatHomeWorkSelectPrompt,
    AssistantCatHomeWorkToolbar,
    HistoryListContent,
    ManuscriptSelectContent,
    ThemeReviewConfirmContent,
    AssistantCatHomeResultDialog,
    IconArrowLeft,
  },
  props: {
    action: {
      type: String as () => AssistantCatHomeAction,
      required: true,
    },
    writerType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      step: "work" as AssistantCatHomeRequestStep,
      novels: [] as Novel[],
      searchQuery: "",
      searchExpanded: false,
      filterWorkStatuses: [] as NovelSetting[],
      filterGenres: [] as NovelSetting[],
      filterCategories: [] as NovelSetting[],
      filterGroups: [] as NovelGroupSummary[],
      selectedNovel: null as Novel | null,
      contentTab: "request" as AssistantCatHomeAssistTab,
      isLoadingNovels: false,
      isLoadingTheme: false,
      isOpeningNovel: false,
      loadError: false,
      assistClient: new AssistClient(),
      resultAssist: null as Assist | null,
      resultPollTimer: null as number | null,
    };
  },
  computed: {
    dialogTitle(): string {
      const key = assistantCatHomeRequestTitleKey(this.action);
      return this.$te(key) ? (this.$t(key) as string) : (this.$t("assistantCatHome.pageTitle") as string);
    },
    headerTitle(): string {
      if (this.resultAssist) {
        return this.resultAssist.requestTitle?.trim() || (this.$t("assistantCatHome.result.pageTitle") as string);
      }
      if (this.step !== "work" && this.contentTab === "history") {
        return this.$t("assistantCatHome.tabs.history") as string;
      }
      return this.dialogTitle;
    },
    prompt(): string {
      const key = assistantCatHomeWorkSelectPromptKey(this.action);
      if (this.$te(key)) {
        return this.$t(key) as string;
      }
      return this.$t("assistantCatHome.workSelect.prompt.fallback") as string;
    },
    manuscriptRequestType(): AssistantCatHomeManuscriptRequestType | null {
      return assistantCatHomeManuscriptRequestType(this.action);
    },
    themeKind(): AssistantCatHomeThemeKind {
      return assistantCatHomeThemeKind(this.action) || "feedback";
    },
    assistFunctionType(): AssistFunctionType | null {
      return assistantCatHomeFunctionType(this.action);
    },
    novelListSortKey(): string {
      return this.$store.getters["novelModule/novelListSortKey"];
    },
    novelListSortOrder(): string {
      return this.$store.getters["novelModule/novelListSortOrder"];
    },
    filteredNovels(): Novel[] {
      return sortAndPinNovels(
        filterAssistantCatHomeNovels(this.novels, {
          searchQuery: this.searchQuery,
          workStatuses: this.filterWorkStatuses,
          genres: this.filterGenres,
          categories: this.filterCategories,
          groups: this.filterGroups,
        }),
        this.novelListSortKey,
        this.novelListSortOrder
      );
    },
    hasActiveFilter(): boolean {
      return hasAssistantCatHomeWorkFilters({
        workStatuses: this.filterWorkStatuses,
        genres: this.filterGenres,
        categories: this.filterCategories,
        groups: this.filterGroups,
      });
    },
    isScriptModeMaskEnabled(): boolean {
      return isScriptModeMaskedAction(this.action);
    },
  },
  created() {
    this.loadNovels();
  },
  beforeDestroy() {
    this.stopResultPolling();
    this.resetAssistContext();
  },
  methods: {
    onMaskClick() {
      if (!this.isSpScreen) {
        this.$emit("close");
      }
    },
    onHeaderBack() {
      if (this.resultAssist) {
        this.onCloseResult();
        return;
      }
      if (this.step === "work") {
        this.$emit("close");
        return;
      }
      this.onBackToWork();
    },
    async loadNovels() {
      this.isLoadingNovels = true;
      this.loadError = false;
      try {
        await Promise.all([
          this.$store.dispatch("novelGroupModule/initialize"),
          this.$store.dispatch("novelModule/initialize"),
          this.$store.dispatch("novelModule/initializeNovelListSettings"),
        ]);
        this.novels = (this.$store.getters["novelModule/novels"] as Novel[]) || [];
      } catch (error) {
        this.loadError = true;
        logError({ module: "AssistantCatHomeRequestDialog", action: "loadNovels" }, "作品一覧の取得に失敗", error);
      } finally {
        this.isLoadingNovels = false;
      }
    },
    async onOpenFilter() {
      const filterDialog = new Dialog(NovelFilterDialog);
      const result: NovelFilterDialogResult | null = await filterDialog.show({
        workStatuses: this.filterWorkStatuses,
        genres: this.filterGenres,
        categories: this.filterCategories,
        groups: this.filterGroups,
      });
      if (result) {
        this.filterWorkStatuses = result.workStatuses;
        this.filterGenres = result.genres;
        this.filterCategories = result.categories;
        this.filterGroups = result.groups;
      }
    },
    isNovelScriptModeMasked(novel: Novel): boolean {
      return this.isScriptModeMaskEnabled && isScriptModeNovel(novel);
    },
    async onSelectNovel(novel: Novel) {
      if (this.isOpeningNovel) {
        return;
      }
      if (this.isNovelScriptModeMasked(novel)) {
        this.$notify({
          title: this.$t("assistantCatHome.workSelect.validation.scriptModeDisabled") as string,
          type: "error",
        });
        return;
      }
      this.contentTab = "request";
      if (this.manuscriptRequestType) {
        this.selectedNovel = novel;
        await this.$store.dispatch("assistSelectManuscriptModule/setSelectedNovelIdOnly", novel.novelId);
        this.step = "manuscript";
        return;
      }
      if (assistantCatHomeThemeKind(this.action)) {
        this.isOpeningNovel = true;
        this.isLoadingTheme = true;
        try {
          const themeStore = useThemeStore();
          await themeStore.fetchTheme(novel.novelId);
          const error = validateThemeAssistHome({
            novel,
            details: themeStore.theme?.detail,
            kind: this.themeKind,
          });
          if (error) {
            await this.showThemeValidationError(error);
            return;
          }
          this.selectedNovel = novel;
          await this.$store.dispatch("assistSelectManuscriptModule/setSelectedNovelIdOnly", novel.novelId);
          this.step = "theme";
        } catch (error) {
          logError({ module: "AssistantCatHomeRequestDialog", action: "fetchTheme" }, "企画の取得に失敗", error);
        } finally {
          this.isLoadingTheme = false;
          this.isOpeningNovel = false;
        }
      }
    },
    resetAssistContext() {
      this.$store.dispatch("assistSelectManuscriptModule/resetState");
      this.$store.dispatch("assistHistory/stopPolling");
    },
    resetToWork() {
      this.onCloseResult();
      this.step = "work";
      this.selectedNovel = null;
      this.contentTab = "request";
      this.resetAssistContext();
    },
    onBackToWork() {
      this.resetToWork();
    },
    onRequestSucceeded() {
      this.contentTab = "history";
      this.$notify({
        title: this.$t("assistantCatHome.requestSubmitted") as string,
        type: "success",
      });
    },
    async onHistorySelect(history: HistoryItem) {
      if (!this.selectedNovel || !history?.id) {
        return;
      }
      try {
        const assist = await this.assistClient.getAssistLog({
          novelId: this.selectedNovel.novelId,
          assistId: history.id,
          functionType: history.functionType,
        });
        this.resultAssist = assist;
        this.startResultPolling();
      } catch (error) {
        logError({ module: "AssistantCatHomeRequestDialog", action: "onHistorySelect" }, "過去ログの取得に失敗", error);
      }
    },
    onCloseResult() {
      this.stopResultPolling();
      this.resultAssist = null;
    },
    stopResultPolling() {
      if (this.resultPollTimer != null) {
        window.clearInterval(this.resultPollTimer);
        this.resultPollTimer = null;
      }
    },
    startResultPolling() {
      this.stopResultPolling();
      const assist = this.resultAssist;
      if (!assist?.assistId || !assist.novelId || !assist.functionType) {
        return;
      }
      if (assist.completed || assist.isError) {
        return;
      }
      const startedAt = Date.now();
      this.resultPollTimer = window.setInterval(async () => {
        if (Date.now() - startedAt > 120000) {
          this.stopResultPolling();
          return;
        }
        if (!this.resultAssist?.assistId) {
          this.stopResultPolling();
          return;
        }
        try {
          const log = await this.assistClient.getAssistLog({
            novelId: assist.novelId,
            assistId: assist.assistId,
            functionType: assist.functionType,
          });
          this.resultAssist = log;
          if (log.completed || log.isError) {
            this.stopResultPolling();
          }
        } catch (error) {
          logError({ module: "AssistantCatHomeRequestDialog", action: "pollAssistLog" }, "アシストログの再取得に失敗", error);
        }
      }, 2000);
    },
    async showThemeValidationError(
      error: "fanFictionNotSupported" | "missingNovelSettings" | "missingSynopsis" | "exceededMaxCharacters" | "missingTheme"
    ) {
      if (error === "fanFictionNotSupported") {
        await showNotifyDialog({
          title: this.$t("themeMatching.validation.fanFictionTitle") as string,
          content: this.$t("themeMatching.validation.fanFictionDisabled") as string,
        });
        return;
      }
      if (error === "missingNovelSettings") {
        await showNotifyDialog({
          title: this.$t("themeReview.validation.novelInfoTitle") as string,
          content: this.$t("themeReview.validation.novelInfoMessage") as string,
        });
        return;
      }
      if (error === "missingSynopsis") {
        await showNotifyDialog({
          title: this.$t("themeReview.validation.requiredFieldsTitle") as string,
          content: this.$t("themeReview.validation.requiredFieldsMessage", {
            fields: `「${this.$t("themePage.synopsisTitle")}」`,
          }) as string,
        });
        return;
      }
      if (error === "exceededMaxCharacters") {
        await showNotifyDialog({
          content: this.$t("assistantCatHome.workSelect.validation.exceededMaxCharacters", {
            max: THEME_ASSIST_MAX_TOTAL_CHARS.toLocaleString("ja-JP"),
          }) as string,
        });
        return;
      }
      await showNotifyDialog({
        content: this.$t("themeReview.validation.fetchErrorMessage") as string,
      });
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.request-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: var(--color-overlay);
}

.request-dialog {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  background: var(--color-background-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.request-dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 20px 48px 0;
}

.request-dialog__title {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: 1.4;
  text-align: center;
}

.request-dialog__close,
.request-dialog__back {
  position: absolute;
  top: 16px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  line-height: 1;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.request-dialog__close {
  right: 16px;
  font-size: var(--font-size-xl);
}

.request-dialog__back {
  left: 16px;
}

.request-dialog__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 16px 16px 24px;
  overflow: hidden;
}

.request-dialog__body--embed {
  overflow-y: auto;
}

.request-dialog__status {
  padding: 24px 8px;
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  text-align: center;
}

.request-dialog__retry {
  position: relative;
  margin-top: 12px;
  padding: 8px 16px;
  border: none;
  border-radius: var(--radius-rounded);
  background: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;
}

.request-dialog__novels {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  margin: 0 -16px;
  overflow-y: auto;
}

.request-dialog__novel--stripe {
  background: var(--color-surface-base);
}

.request-dialog__body--embed :deep(.assist-history-card) {
  background: var(--color-surface-base);
}

.request-dialog__body--embed :deep(.assist-history-card__content) {
  background: var(--color-background-normal);
}

@include mq("sp") {
  .request-dialog-mask {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
    background-color: var(--color-background-normal);
  }

  .request-dialog {
    width: 100%;
    max-width: none;
    max-height: none;
    height: 100%;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: var(--color-background-normal);
  }

  .request-dialog__header {
    height: calc(44px + env(safe-area-inset-top, 0px));
    padding: env(safe-area-inset-top, 0px) 48px 0;
    box-shadow: var(--shadow-md);
    background: var(--color-background-normal);
  }

  .request-dialog__header--variant {
    background: var(--color-background-invert);
    box-shadow: none;
    color: var(--color-text-invert);
  }

  .request-dialog__title {
    font-size: var(--font-size-md);
  }

  .request-dialog__header--variant .request-dialog__title,
  .request-dialog__header--variant .request-dialog__back {
    color: var(--color-text-invert);
  }

  .request-dialog__close {
    display: none;
  }

  .request-dialog__back,
  .request-dialog__close {
    top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .request-dialog__back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;

    ::v-deep .figma-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
    }
  }

  .request-dialog__status {
    padding: 24px 16px;
  }

  .request-dialog__body {
    gap: 24px;
    padding: 16px 0 0;
    background: var(--color-background-normal);
  }

  .request-dialog__body--embed {
    gap: 0;
    padding: 0;
  }

  .request-dialog__body--history {
    padding: 16px;

    :deep(.history-list-content__list) {
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  .request-dialog__body--embed :deep(.history-list-content__empty-icon),
  .request-dialog__body--embed :deep(.history-list-content__error-icon) {
    display: none;
  }

  .request-dialog__novels {
    margin: 0;
  }
}
</style>
